import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useAccount } from "wagmi";
import { useMarketInfo, useMarketOdds } from "@/hooks/useMarkets";
import { useUSDTBalance, useUSDTAllowance, useUSDTApprove } from "@/hooks/useUSDT";
import { usePlaceBet, useClaimWinnings, useUnclaimedWinnings, useUserBets } from "@/hooks/useBetting";
import { formatUSDT, parseUSDT, formatOdds } from "@/lib/contracts";
import CategoryTag from "@/components/ui/CategoryTag";
import { categorize } from "@/lib/categories";

export default function MarketPage() {
  const { address: marketAddress } = useParams<{ address: string }>();
  const { address: userAddress, isConnected } = useAccount();

  const typedMarketAddress = marketAddress as `0x${string}` | undefined;
  const typedUserAddress = userAddress as `0x${string}` | undefined;

  const { data: marketInfo, isLoading } = useMarketInfo(typedMarketAddress);
  const { data: odds } = useMarketOdds(typedMarketAddress);
  const { data: balance } = useUSDTBalance(typedUserAddress);
  const { data: allowance } = useUSDTAllowance(typedUserAddress, typedMarketAddress);
  const { data: userBets } = useUserBets(typedMarketAddress, typedUserAddress);
  const { data: unclaimedWinnings } = useUnclaimedWinnings(typedMarketAddress, typedUserAddress);

  const { approve, isPending: isApproving } = useUSDTApprove();
  const { placeBet, isPending: isBetting, isSuccess: betSuccess, reset: resetBet } = usePlaceBet();
  const { claimWinnings, isPending: isClaiming, isSuccess: claimSuccess } = useClaimWinnings();

  const [betAmount, setBetAmount] = useState("");
  const [selectedOutcome, setSelectedOutcome] = useState<boolean | null>(null);

  // Reset form after successful bet
  useEffect(() => {
    if (betSuccess) {
      setBetAmount("");
      setSelectedOutcome(null);
      setTimeout(() => resetBet(), 2000);
    }
  }, [betSuccess, resetBet]);

  if (isLoading || !marketInfo) {
    return (
      <div className="max-w-3xl mx-auto">
        <div className="card animate-pulse p-6">
          <div className="h-8 bg-gray-200 rounded w-3/4 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2 mb-6"></div>
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="h-32 bg-gray-200 rounded-lg"></div>
            <div className="h-32 bg-gray-200 rounded-lg"></div>
          </div>
        </div>
      </div>
    );
  }

  const [question, endTime, , totalYesBets, totalNoBets, isResolved, winningOutcome] = marketInfo;
  const totalPool = totalYesBets + totalNoBets;
  const endDate = new Date(Number(endTime) * 1000);
  const isExpired = endDate < new Date();
  const isOpen = !isResolved && !isExpired;

  const yesPercent = totalPool > 0n ? Number((totalYesBets * 100n) / totalPool) : 50;

  const betAmountBigInt = betAmount ? parseUSDT(betAmount) : 0n;
  const needsApproval = allowance !== undefined && betAmountBigInt > allowance;
  const hasInsufficientBalance = balance !== undefined && betAmountBigInt > balance;

  const handleApprove = () => {
    if (typedMarketAddress) {
      // Approve max uint256 for convenience
      approve(typedMarketAddress, BigInt("0xffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff"));
    }
  };

  const handlePlaceBet = () => {
    if (typedMarketAddress && selectedOutcome !== null && betAmountBigInt > 0n) {
      placeBet(typedMarketAddress, selectedOutcome, betAmountBigInt);
    }
  };

  const handleClaim = () => {
    if (typedMarketAddress) {
      claimWinnings(typedMarketAddress);
    }
  };

  return (
    <div className="max-w-3xl mx-auto">
      {/* Back Link */}
      <Link to="/" className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-4">
        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back to Markets
      </Link>

      <div className="card p-6 mb-6">
        {/* Status Badge */}
        <div className="flex justify-between items-start mb-4">
          {isResolved ? (
            <span className={`badge ${winningOutcome ? "badge-green" : "badge-red"}`}>
              Resolved: {winningOutcome ? "YES" : "NO"}
            </span>
          ) : isOpen ? (
            <span className="badge badge-green">Open for Predictions</span>
          ) : (
            <span className="badge badge-yellow">Closed</span>
          )}
          <span className="text-sm text-gray-500">
            {isOpen ? `Ends ${endDate.toLocaleDateString()}` : `Ended ${endDate.toLocaleDateString()}`}
          </span>
        </div>

        {/* Question */}
        <span className="mb-2 inline-block">
          <CategoryTag category={categorize(question)} />
        </span>
        <h1 className="text-2xl font-bold text-gray-900 mb-6">{question}</h1>

        {/* Odds Display */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <button
            onClick={() => isOpen && setSelectedOutcome(true)}
            disabled={!isOpen}
            className={`p-4 rounded-lg border-2 transition-all ${
              selectedOutcome === true
                ? "border-success bg-success/10"
                : "border-gray-200 hover:border-success/50"
            } ${!isOpen ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
          >
            <div className="text-sm text-success font-medium mb-1">YES</div>
            <div className="num text-2xl font-bold text-success">
              {odds ? formatOdds(odds[0]) : "-"}
            </div>
            <div className="num text-sm text-gray-500 mt-1">{formatUSDT(totalYesBets)} USDT</div>
          </button>

          <button
            onClick={() => isOpen && setSelectedOutcome(false)}
            disabled={!isOpen}
            className={`p-4 rounded-lg border-2 transition-all ${
              selectedOutcome === false
                ? "border-danger bg-danger/10"
                : "border-gray-200 hover:border-danger/50"
            } ${!isOpen ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
          >
            <div className="text-sm text-danger font-medium mb-1">NO</div>
            <div className="num text-2xl font-bold text-danger">
              {odds ? formatOdds(odds[1]) : "-"}
            </div>
            <div className="num text-sm text-gray-500 mt-1">{formatUSDT(totalNoBets)} USDT</div>
          </button>
        </div>

        {/* Pool Progress */}
        <div className="mb-4">
          <div className="flex justify-between text-sm text-gray-600 mb-2">
            <span>Total Pool</span>
            <span className="num font-semibold">{formatUSDT(totalPool)} USDT</span>
          </div>
          <div className="h-3 bg-gray-200 rounded-full overflow-hidden flex">
            <div className="bg-success" style={{ width: `${yesPercent}%` }} />
            <div className="bg-danger" style={{ width: `${100 - yesPercent}%` }} />
          </div>
          <div className="flex justify-between text-xs text-gray-500 mt-1">
            <span className="num">{yesPercent.toFixed(1)}% YES</span>
            <span className="num">{(100 - yesPercent).toFixed(1)}% NO</span>
          </div>
        </div>

        {/* Market Terms */}
        <div className="text-xs text-gray-500 mb-6">
          Winners split 98% of the pool · 2% platform fee on resolution
        </div>

        {/* Betting Form */}
        {isConnected && isOpen && (
          <div className="border-t border-gray-200 pt-6">
            <h3 className="font-semibold text-gray-900 mb-4">Make Your Prediction</h3>

            {/* Amount Input */}
            <div className="mb-4">
              <label className="block text-sm text-gray-600 mb-1">Amount (USDT)</label>
              <div className="relative">
                <input
                  type="number"
                  value={betAmount}
                  onChange={(e) => setBetAmount(e.target.value)}
                  placeholder="0.00"
                  min="1"
                  step="1"
                  className="input pr-20"
                />
                <button
                  onClick={() => balance && setBetAmount((Number(balance) / 1e6).toString())}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-primary-600 hover:text-primary-700"
                >
                  MAX
                </button>
              </div>
              {balance !== undefined && (
                <div className="text-xs text-gray-500 mt-1">
                  Balance: {formatUSDT(balance)} USDT
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="space-y-2">
              {hasInsufficientBalance && betAmount && (
                <div className="text-danger text-sm">Insufficient USDT balance</div>
              )}

              {needsApproval && !hasInsufficientBalance && (
                <button
                  onClick={handleApprove}
                  disabled={isApproving}
                  className="btn btn-outline w-full"
                >
                  {isApproving ? "Approving..." : "Approve USDT"}
                </button>
              )}

              <button
                onClick={handlePlaceBet}
                disabled={
                  selectedOutcome === null ||
                  !betAmount ||
                  needsApproval ||
                  hasInsufficientBalance ||
                  isBetting
                }
                className={`btn w-full ${
                  selectedOutcome === true ? "btn-success" : selectedOutcome === false ? "btn-danger" : "btn-primary"
                }`}
              >
                {isBetting
                  ? "Making Prediction..."
                  : betSuccess
                  ? "Prediction Made!"
                  : selectedOutcome === null
                  ? "Select YES or NO"
                  : `Predict ${betAmount || "0"} USDT on ${selectedOutcome ? "YES" : "NO"}`}
              </button>
            </div>
          </div>
        )}

        {/* Claim Winnings */}
        {isConnected && isResolved && unclaimedWinnings !== undefined && unclaimedWinnings > 0n && (
          <div className="border-t border-gray-200 pt-6">
            <div className="bg-success/10 rounded-lg p-4 text-center">
              <div className="text-success font-medium mb-2">You have unclaimed winnings!</div>
              <div className="num text-2xl font-bold text-success mb-4">
                {formatUSDT(unclaimedWinnings)} USDT
              </div>
              <button
                onClick={handleClaim}
                disabled={isClaiming}
                className="btn btn-success"
              >
                {isClaiming ? "Claiming..." : claimSuccess ? "Claimed!" : "Claim Winnings"}
              </button>
            </div>
          </div>
        )}

        {/* User Bets */}
        {userBets && userBets.length > 0 && (
          <div className="border-t border-gray-200 pt-6 mt-6">
            <h3 className="font-semibold text-gray-900 mb-4">Your Predictions</h3>
            <div className="space-y-2">
              {userBets.map((bet, i) => (
                <div
                  key={i}
                  className={`flex justify-between items-center p-3 rounded-lg ${
                    bet.outcome ? "bg-success/10" : "bg-danger/10"
                  }`}
                >
                  <div>
                    <span className={`font-medium ${bet.outcome ? "text-success" : "text-danger"}`}>
                      {bet.outcome ? "YES" : "NO"}
                    </span>
                    <span className="num text-gray-600 ml-2">{formatUSDT(bet.amount)} USDT</span>
                  </div>
                  {bet.claimed && <span className="badge badge-gray">Claimed</span>}
                </div>
              ))}
            </div>
          </div>
        )}

        {!isConnected && (
          <div className="border-t border-gray-200 pt-6 text-center text-gray-500">
            Connect your wallet to make predictions
          </div>
        )}
      </div>
    </div>
  );
}
